import React, { useEffect, useState } from 'react';
import { Modal } from '../../components/Modal';
import Button from '../../components/ui/Button';
import {
  applyPackagingCorrection,
  previewPackagingCorrection,
  type PackagingCorrectionFields,
  type PackagingCorrectionPreview,
  type PackagingCorrectionResult,
  type PackagingLineCells,
  type PackagingPriceTiers,
} from '../../lib/vendorInvoiceApi';
import { REPRICE_ASK_ABOVE_PCT } from '../../utils/repricePolicy';

/**
 * VI-7 (D29) — CORRECT THE PACKAGING OF A LINE ALREADY IN INVENTORY.
 *
 * Deborah, meeting 2 (14 Sep 2026): Albrosco prints no unit sizes, so she typed
 * them in (148 g tuna flake) after stock-in, and nothing reached the Master
 * Catalogue. This is the deliberate way: type the corrected cells, Preview (a
 * dry run of the real sync on the server), then Apply. Apply updates the line,
 * item memory at reviewer weight and the catalogue row, replacing a conversion
 * the row already holds (PROVISIONAL O-10), with an audit entry. Stock on hand,
 * stock layers and posted money never change (Q10).
 *
 * Undo is a second correction back to the old cells, previewed like the first.
 */

export interface PackagingCorrectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber: string | null;
  lineIndex: number;
  /** The line as the grid shows it (saved values: the button waits for Save). */
  line: any;
  formatMoney: (value: unknown, currency?: string | null) => string;
  /** After a successful apply, so the grid reloads the corrected line. */
  onApplied?: (result: PackagingCorrectionResult) => void;
}

type Draft = { splitUow: string; splitPackCount: string; splitUnitSize: string };

const text = (v: unknown) => (v == null ? '' : String(v));
const draftFrom = (cells: Partial<PackagingLineCells> | any): Draft => ({
  splitUow: text(cells?.splitUow),
  splitPackCount: text(cells?.splitPackCount),
  splitUnitSize: text(cells?.splitUnitSize),
});
const fieldsFrom = (d: Draft): PackagingCorrectionFields => ({
  splitUow: d.splitUow.trim() || null,
  splitPackCount: d.splitPackCount.trim() || null,
  splitUnitSize: d.splitUnitSize.trim() || null,
});
const sameDraft = (a: Draft, b: Draft) =>
  a.splitUow.trim() === b.splitUow.trim() &&
  a.splitPackCount.trim() === b.splitPackCount.trim() &&
  a.splitUnitSize.trim() === b.splitUnitSize.trim();

const OUTCOME: Record<string, string> = {
  apply: 'The catalogue row takes the corrected packaging and its prices are worked out again.',
  insert: 'A new catalogue row is written with the corrected packaging.',
  hold: 'A newer invoice prices this row, so its price and stated packaging stay. A different pack is listed as a conflict to settle on the row.',
  park: `The price would move by more than ${REPRICE_ASK_ABOVE_PCT}%, so it waits on the row for a person. The stated packaging stays until then; a different pack is listed as a conflict.`,
  excluded: 'This line is switched off from the catalogue sync, so the catalogue does not change.',
  no_row: 'This line has no catalogue row (no price or no name), so only the line and item memory change.',
};

// A hold or park's wording by its reason, where the reason changes what the sentence must say (D170.6).
const KEPT_REASON: Record<string, string> = {
  fresh_item: 'This is a fresh item, priced by hand monthly, so the invoice does not reprice it; a different price waits on the row for a person. The stated packaging stays; a different pack is listed as a conflict.',
  newer_proposal: "This invoice prices the row, but a newer invoice's price is already waiting on it, so its price and stated packaging stay. A different pack is listed as a conflict to settle on the row.",
  // D31 / Q6, VI-9.
  returned_line: 'This line states no quantity (a return or a short delivery), so the row\'s price and stated packaging stay. A different pack is listed as a conflict to settle on the row.',
};

const EFFECT: Record<string, string> = {
  updated: 'updated',
  created: 'created',
  packaging_only: 'packaging only',
  removed: 'removed (left behind under the old packaging)',
  left_unchanged: 'left as it is (another invoice has priced it)',
};

const FIELD_LABEL: Record<string, string> = {
  splitUow: 'Pack Unit',
  splitPackCount: 'Units / Case',
  splitUnitSize: 'Unit Size',
  splitItemUow: 'Unit',
  splitItemUnits: 'Total Units (invoice record only)',
};

function Change({ label, from, to }: { label: string; from: string; to: string }) {
  const changed = from !== to;
  return (
    <tr className={changed ? 'bg-emerald-50' : undefined}>
      <th scope="row" className="py-1 pr-3 text-left font-medium text-slate-500">
        {label}
      </th>
      <td className="py-1 pr-3 tabular-nums text-slate-600">{from || '—'}</td>
      <td className={`py-1 tabular-nums ${changed ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{to || '—'}</td>
    </tr>
  );
}

function priceRows(
  before: PackagingPriceTiers,
  after: PackagingPriceTiers,
  fmt: PackagingCorrectionDialogProps['formatMoney']
) {
  const tt = (n: number | null | undefined) => (n == null ? '' : fmt(n, 'TTD'));
  const row = (label: string, pick: (t: NonNullable<PackagingPriceTiers>) => string) => (
    <Change key={label} label={label} from={before ? pick(before) : ''} to={after ? pick(after) : ''} />
  );
  return [
    row('Price', (t) => (t.price == null ? '' : `${fmt(t.price, t.currency)} per ${t.unit || 'unit'}`)),
    row('Per pack', (t) => tt(t.perPack)),
    row('Per unit', (t) => tt(t.perUnit)),
    row('Per kg', (t) => tt(t.perKg)),
    row('Per lb', (t) => tt(t.perLb)),
    row('Per L', (t) => tt(t.perL)),
  ];
}

export function PackagingCorrectionDialog({
  isOpen,
  onClose,
  invoiceId,
  invoiceNumber,
  lineIndex,
  line,
  formatMoney,
  onApplied,
}: PackagingCorrectionDialogProps) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(line));
  const [previewed, setPreviewed] = useState<{ draft: Draft; data: PackagingCorrectionPreview } | null>(null);
  const [result, setResult] = useState<PackagingCorrectionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(draftFrom(line));
    setPreviewed(null);
    setResult(null);
    setError(null);
  }, [isOpen, line, lineIndex, invoiceId]);

  const lineNo = line?.lineNo ?? lineIndex + 1;
  const name = String(line?.itemName || line?.description || '').trim();
  const current = previewed && sameDraft(previewed.draft, draft) ? previewed.data : null;

  async function runPreview() {
    setBusy(true);
    setError(null);
    try {
      const res = await previewPackagingCorrection(invoiceId, lineIndex, fieldsFrom(draft));
      setPreviewed({ draft, data: res.data });
    } catch (err: any) {
      setError(err?.message || 'The preview could not be worked out.');
    } finally {
      setBusy(false);
    }
  }

  async function runApply() {
    if (!current) return;
    setBusy(true);
    setError(null);
    try {
      const res = await applyPackagingCorrection(invoiceId, lineIndex, fieldsFrom(draft), current.line.before);
      setResult(res.data);
      setPreviewed(null);
      onApplied?.(res.data);
    } catch (err: any) {
      setError(err?.message || 'The correction could not be applied.');
    } finally {
      setBusy(false);
    }
  }

  function startUndo() {
    if (!result) return;
    setDraft(draftFrom(result.line.before));
    setResult(null);
    setPreviewed(null);
  }

  const input = (key: keyof Draft, label: string, hint: string) => (
    <label className="flex min-w-0 flex-col gap-1 text-xs font-medium text-slate-600">
      {label}
      <input
        value={draft[key]}
        onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
        disabled={busy || !!result}
        placeholder={hint}
        className="rounded border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
      />
    </label>
  );

  const cat = current?.catalogue;
  return (
    <Modal
      isOpen={isOpen}
      title={`Correct packaging — line ${lineNo}${name ? ` (${name})` : ''}`}
      onClose={() => {
        if (!busy) onClose();
      }}
      maxWidthClass="max-w-3xl"
    >
      <div className="grid gap-4 text-sm">
        <p className="text-slate-600">
          Invoice {invoiceNumber || '—'} is already in Inventory. A correction updates this line, item memory and
          the Master Catalogue row. <strong>Stock on hand, stock layers and money already posted do not change.</strong>
        </p>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {input('splitUow', 'Pack Unit', 'CASE')}
          {input('splitPackCount', 'Units / Case', '12')}
          {input('splitUnitSize', 'Unit Size', '148 G')}
        </div>

        {error ? (
          <p role="alert" className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
            {error}
          </p>
        ) : null}

        {result ? (
          <section aria-label="Correction applied" className="grid gap-2 rounded border border-emerald-200 bg-emerald-50 p-3">
            <p className="font-semibold text-emerald-900">Applied to line {result.lineNo}.</p>
            <p className="text-emerald-900">
              Master Catalogue: {result.catalogue.repriced} updated, {result.catalogue.created} created,{' '}
              {result.catalogue.heldOlder} packaging only, {result.catalogue.parked} price parked,{' '}
              {result.catalogue.relocated} removed.
              {result.catalogue.replacedConversions.length
                ? ` Replaced: ${result.catalogue.replacedConversions.join('; ')}.`
                : ''}
            </p>
            <p className="text-emerald-900">
              Item memory:{' '}
              {result.memory.sharedCodeWith
                ? `not changed (this supplier code is also ${result.memory.sharedCodeWith})`
                : result.memory.recorded
                  ? 'learned the corrected packaging'
                  : 'nothing recorded (no supplier matched)'}
              .
            </p>
            <div>
              <Button size="sm" onClick={startUndo}>
                Undo (correct back to the old cells)
              </Button>
            </div>
          </section>
        ) : null}

        {cat ? (
          <section aria-label="Preview" className="grid gap-3">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="pb-1">On the invoice line</th>
                  <th className="pb-1">Now</th>
                  <th className="pb-1">After</th>
                </tr>
              </thead>
              <tbody>
                {(Object.keys(FIELD_LABEL) as (keyof PackagingLineCells)[]).map((k) => (
                  <Change
                    key={k}
                    label={FIELD_LABEL[k]}
                    from={text(current!.line.before[k])}
                    to={text(current!.line.after[k])}
                  />
                ))}
              </tbody>
            </table>

            <div className="grid gap-1">
              <p className="font-semibold text-slate-800">Master Catalogue</p>
              <p className="text-slate-600">
                {((cat.outcome === 'park' || cat.outcome === 'hold') && KEPT_REASON[cat.reason || '']) || OUTCOME[cat.outcome] || cat.outcome}
              </p>
              {cat.identity?.changed ? (
                <p className="text-amber-800">
                  The row&apos;s identity changes with its packaging (a different pack is a different product, D24).
                </p>
              ) : null}
              {cat.rowsAffected.map((r) => (
                <p key={`${r.id}-${r.effect}`} className="text-slate-600">
                  Row {r.itemName || r.id || 'new'}: {EFFECT[r.effect] || r.effect}
                </p>
              ))}
            </div>

            {cat.conversions ? (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="pb-1">Conversions</th>
                    <th className="pb-1">Now</th>
                    <th className="pb-1">After</th>
                  </tr>
                </thead>
                <tbody>
                  <Change label="Stated" from={cat.conversions.before.join('; ')} to={cat.conversions.after.join('; ')} />
                  {cat.conversions.replaced.length ? (
                    <tr>
                      <th scope="row" className="py-1 pr-3 text-left font-medium text-slate-500">
                        Replaced
                      </th>
                      <td colSpan={2} className="py-1 text-rose-700 line-through">
                        {cat.conversions.replaced.join('; ')}
                      </td>
                    </tr>
                  ) : null}
                  {cat.conversions.conflicts.length ? (
                    <tr>
                      <th scope="row" className="py-1 pr-3 text-left font-medium text-slate-500">
                        Still conflicts
                      </th>
                      <td colSpan={2} className="py-1 text-amber-800">
                        {cat.conversions.conflicts.join('; ')}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            ) : null}

            {cat.prices ? (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="pb-1">Prices quotes read</th>
                    <th className="pb-1">Now</th>
                    <th className="pb-1">After</th>
                  </tr>
                </thead>
                <tbody>{priceRows(cat.prices.before, cat.prices.after, formatMoney)}</tbody>
                {cat.prices.proposed ? (
                  <caption className="caption-bottom pt-1 text-left text-xs text-amber-800">
                    Not written: at the corrected packaging this line would price{' '}
                    {cat.prices.proposed.perKg != null ? `${formatMoney(cat.prices.proposed.perKg, 'TTD')} per kg` : ''}
                    {cat.prices.proposed.perUnit != null ? ` ${formatMoney(cat.prices.proposed.perUnit, 'TTD')} per unit` : ''}.
                  </caption>
                ) : null}
              </table>
            ) : null}

            <div className="grid gap-1">
              <p className="font-semibold text-slate-800">Item memory (reviewer weight)</p>
              {current!.memory.sharedCodeWith ? (
                <p className="text-amber-800">
                  Not taught: this supplier item code is also {current!.memory.sharedCodeWith}. Item memory is left
                  alone so that product keeps its own packaging; the line and the catalogue row are still corrected.
                </p>
              ) : current!.memory.fields.length ? (
                current!.memory.fields.map((f) => (
                  <p key={f.field} className="text-slate-600">
                    {FIELD_LABEL[f.field] || f.field}: {text(f.from) || '—'} → {text(f.to) || '—'}
                  </p>
                ))
              ) : (
                <p className="text-slate-600">The line already says this; applying re-syncs the catalogue row only.</p>
              )}
            </div>
          </section>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Close
          </Button>
          {!result ? (
            <>
              <Button variant="secondary" onClick={runPreview} disabled={busy}>
                Preview
              </Button>
              <Button variant="primary" onClick={runApply} disabled={busy || !current}>
                Apply correction
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

export default PackagingCorrectionDialog;
